import React from 'react';
import { useTranslation } from 'react-i18next';
import { CarSide } from '../icons/CarSide';

// radius of the pink glow behind the selected number, in the 800 x 300 drawing
export const ZONE_GLOW_R = 58;

const yearBarStyle = bar => {
    if (bar.kind === 'none') {
        return {
            display: 'block',
            width: '64%',
            height: '100%',
            borderRadius: 8,
            background: 'repeating-linear-gradient(135deg,rgba(255,255,255,0.10) 0 6px,transparent 6px 12px)',
            boxShadow: 'inset 0 0 0 1px rgba(255,255,255,0.18)',
        };
    }
    if (bar.kind === 'first')
        return { display: 'block', width: '64%', height: 6, borderRadius: 3, background: '#FFFFFF' };
    if (bar.kind === 'drop')
        return { display: 'block', width: '64%', height: 10, borderRadius: 3, background: '#F59E0B' };
    return {
        display: 'block',
        width: '64%',
        height: `${bar.height}%`,
        borderRadius: '8px 8px 3px 3px',
        background: bar.flag ? '#E83E8C' : '#FFFFFF',
    };
};
export const Stage = ({ history, onZone, onYear }) => {
    const { t } = useTranslation('reportPage');
    const { car, numbers, zones, selectedZone, years, yearsFilter } = history;
    const geo = selectedZone && selectedZone.geo;

    return (
        <div className="stage">
            <div className="stage-top">
                {car && (
                    <div className="hcar">
                        <div className="hcar-logo" aria-hidden="true">
                            {car.brand}
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 4, minWidth: 0 }}>
                            <span className="hcar-model">{car.model}</span>
                            {car.spec && <span className="hcar-spec">{car.spec}</span>}
                        </div>
                    </div>
                )}
                {numbers.length > 0 && (
                    <div className="stage-nums">
                        {numbers.map(number => (
                            <div className="snum" key={number.label}>
                                <span className={`snum-v${number.pink ? ' pink' : ''}`}>{number.value}</span>
                                <span className="snum-l">{number.label}</span>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {zones.length > 0 && (
                <>
                    <div className="carmap">
                        <svg
                            viewBox="0 0 800 300"
                            width="100%"
                            height="100%"
                            role="img"
                            aria-label={t('history.mapAria', {
                                count: zones.length,
                                areas: zones.map(z => z.name).join(', '),
                            })}
                            style={{ position: 'absolute', inset: 0, display: 'block' }}
                        >
                            <CarSide>
                                {/* the selected area glows as the same circle around its number, whatever its shape */}
                                {geo && (
                                    <circle
                                        cx={geo.x}
                                        cy={geo.y}
                                        r={ZONE_GLOW_R}
                                        fill="#E83E8C"
                                        fillOpacity="0.35"
                                        data-testid="zone-glow"
                                    />
                                )}
                            </CarSide>
                        </svg>
                        {zones.map(zone => (
                            <button
                                type="button"
                                key={zone.key}
                                className={`pin${zone.flag ? ' flag' : ''}${zone.on ? ' on' : ''}`}
                                style={{
                                    left: `${(zone.geo.x / 8).toFixed(2)}%`,
                                    top: `${(zone.geo.y / 3).toFixed(2)}%`,
                                }}
                                aria-label={`${zone.n}. ${zone.name}, ${zone.meta}${zone.flag ? `, ${t('history.redFlagTag')}` : ''}`}
                                aria-pressed={zone.on}
                                onClick={() => onZone(zone.key)}
                            >
                                {zone.n}
                            </button>
                        ))}
                    </div>
                    <div className="stage-legend">
                        <span className="lgc">
                            <i />
                            {t('history.legendService')}
                        </span>
                        <span className="lgc">
                            <i className="p" />
                            {t('history.legendFlag')}
                        </span>
                        <span className="lgc noprint">{t('history.legendHint')}</span>
                    </div>
                </>
            )}

            {years.length > 0 && (
                <div className="yrs">
                    <div className="yrs-head">
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                            <span className="yrs-t">{t('history.kmPerYear')}</span>
                            <span className="yrs-s">
                                {yearsFilter ? t('history.yearsSubVisits') : t('history.yearsSubRepairs')}
                            </span>
                        </div>
                        {yearsFilter && (
                            <button
                                type="button"
                                className={`allbtn${years.some(y => y.on) ? '' : ' on'}`}
                                aria-pressed={!years.some(y => y.on)}
                                onClick={() => onYear(null)}
                            >
                                {t('history.allYears')}
                            </button>
                        )}
                    </div>
                    <div style={{ overflowX: 'auto' }}>
                        <div
                            className="yrs-grid"
                            style={{
                                gridTemplateColumns: `repeat(${years.length}, minmax(52px, 1fr))`,
                                minWidth: years.length * 58,
                            }}
                            role="group"
                            aria-label={t('history.filterByYear')}
                        >
                            {years.map(year => (
                                <button
                                    type="button"
                                    key={year.year}
                                    className={`yr${year.on ? ' on' : ''}`}
                                    aria-pressed={year.on}
                                    aria-label={year.aria}
                                    disabled={year.disabled}
                                    onClick={() => onYear(year.year)}
                                >
                                    <span className={`yr-km${year.warn ? ' warn' : ''}${year.dim ? ' dim' : ''}`}>
                                        {year.kmLabel}
                                    </span>
                                    <span className="yr-barbox">
                                        <span style={yearBarStyle(year.bar)} />
                                    </span>
                                    <span className="yr-dots">
                                        {year.dots.map((flag, i) => (
                                            <span className={`ydot${flag ? ' flag' : ''}`} key={i} />
                                        ))}
                                    </span>
                                    <span className="yr-y">{year.year}</span>
                                </button>
                            ))}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};
